import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Skeleton } from "@mui/material";

import { PixRounded } from "@mui/icons-material";

import {
    IconCreditCard,
    IconLock,
    IconMapPin,
    IconReceipt2,
    IconRefresh,
    IconShoppingBag,
    IconWallet,
    IconWorld,
} from "@tabler/icons-react";

import Parser from "@helpers/parser";

import { Button, ButtonGroup, Card, ErrorMsg, Text } from "@fragments";

import { getInternationalCountries } from "./countries";

import "./styles.scss";

// Sem equivalente Tabler para a marca Pix — o glifo do MUI segue como exceção.
// Os rótulos são resolvidos no render para acompanhar o idioma ativo.
const getMethodOptions = (t) => ({
    pix: { label: t("managePlan.checkoutPreview.methods.pix"), icon: <PixRounded /> },
    credit: { label: t("managePlan.checkoutPreview.methods.credit"), icon: <IconCreditCard /> },
    debit: { label: t("managePlan.checkoutPreview.methods.debit"), icon: <IconCreditCard /> },
    boleto: { label: t("managePlan.checkoutPreview.methods.boleto"), icon: <IconReceipt2 /> },
    manual: { label: t("managePlan.checkoutPreview.methods.manual"), icon: <IconWallet /> },
});

const getRegionOptions = (t) => [
    { value: "national", label: t("managePlan.checkoutPreview.regions.national"), icon: <IconMapPin /> },
    { value: "international", label: t("managePlan.checkoutPreview.regions.international"), icon: <IconWorld /> },
];

// Mockup ilustrativo do checkout (pay.ginastee.com): seletor de método,
// resumo de preço, parcelas e botão de assinatura — arte estática, exceto o
// seletor Brasil/Internacional. installments_list[i] guarda o TOTAL da
// transação em i + 1 parcelas, então cada linha exibe total / (i + 1).
// A região é controlada pelo modal (props region/onRegionChange) — a prévia
// acompanha a seção de precificação que o usuário edita no formulário.
export default function CheckoutPreview({
    info,
    methods = [],
    intervalLabel,
    stripeConnected,
    simulation,
    region = "national",
    onRegionChange,
    onRetry,
}) {
    const { t } = useTranslation("contentModals");
    const methodOptionsMap = useMemo(() => getMethodOptions(t), [t]);
    const regionOptions = useMemo(() => getRegionOptions(t), [t]);
    const internationalCountries = useMemo(() => getInternationalCountries(t), [t]);
    const [showAllCountries, setShowAllCountries] = useState(false);

    const price = parseInt(info?.price, 10) || 0;
    const usdPrice = parseInt(info?.usd_price, 10) || 0;
    const isLifetime = info?.interval === "lifetime";
    const label = info?.label?.trim() || t("managePlan.checkoutPreview.fallbackLabel");

    const hasInternational = !!stripeConnected;
    const isInternational = hasInternational && region === "international";
    const hasCredit = methods.includes("credit");

    const list =
        Array.isArray(simulation?.list) && simulation.list.length > 0
            ? simulation.list
            : null;

    // Parcelamento só existe no crédito nacional — o checkout internacional
    // (Stripe) e os demais métodos cobram à vista.
    const installmentsList =
        !isInternational && hasCredit && list && list.length > 1 ? list : null;

    const maxCount = installmentsList ? installmentsList.length : 1;
    const headlineTotal = installmentsList
        ? installmentsList[installmentsList.length - 1]
        : price;
    const perInstallment = Math.round(headlineTotal / maxCount) || 0;

    const methodOptions = methods
        .filter(method => methodOptionsMap[method])
        .map(method => ({ value: method, ...methodOptionsMap[method] }));

    const selectedMethod = methodOptions.some(option => option.value === "credit")
        ? "credit"
        : methodOptions[0]?.value;

    const showInstallmentsSection =
        !isInternational &&
        hasCredit &&
        price > 0 &&
        ((simulation?.loading && info?.enable_installments) ||
            installmentsList ||
            simulation?.error);

    const heroReady = isInternational ? usdPrice > 0 : price > 0;
    const displayPrice = isInternational
        ? `US$ ${Parser.price.usd(usdPrice)}`
        : `R$ ${Parser.price.brl(installmentsList ? perInstallment : price)}`;

    // Primeiras parcelas à esquerda, restantes à direita (6 + 6 no máximo).
    const splitAt = installmentsList ? Math.ceil(installmentsList.length / 2) : 0;
    const installmentColumns = installmentsList
        ? [installmentsList.slice(0, splitAt), installmentsList.slice(splitAt)]
            .filter(column => column.length > 0)
        : [];

    return (
        <Card
            className="checkout-preview"
            eyebrow={t("managePlan.checkoutPreview.eyebrow")}
            title={label}
            icon={<IconShoppingBag />}
            footer={
                <div className="checkout-preview-footer">
                    <Text.caption contrast={5} left={<IconLock />}>
                        {t("managePlan.checkoutPreview.secure")}
                    </Text.caption>
                    {info?.autorenew && !isLifetime && (
                        <Text.caption contrast={5} left={<IconRefresh />}>
                            {t("managePlan.checkoutPreview.autorenew")}
                        </Text.caption>
                    )}
                </div>
            }
        >
            <div className="checkout-preview-body">
                {hasInternational && (
                    <ButtonGroup
                        grow
                        size="small"
                        options={regionOptions}
                        value={isInternational ? "international" : "national"}
                        onChange={onRegionChange}
                    />
                )}

                {heroReady ? (
                    <div className="checkout-preview-hero">
                        {installmentsList && (
                            <Text.caption contrast={6}>{t("managePlan.checkoutPreview.installmentsPrefix", { count: maxCount })}</Text.caption>
                        )}

                        <div className="checkout-preview-hero-row">
                            <Text.h3 contrast={10}>{displayPrice}</Text.h3>
                            {!isLifetime && intervalLabel && (
                                <Text.caption contrast={6}>/ {intervalLabel}</Text.caption>
                            )}
                        </div>

                        {installmentsList && (
                            <Text.caption contrast={5}>
                                {t("managePlan.checkoutPreview.total", { value: Parser.price.brl(headlineTotal) })}
                            </Text.caption>
                        )}
                    </div>
                ) : (
                    <Text.caption contrast={5}>
                        {isInternational
                            ? t("managePlan.checkoutPreview.emptyInternational")
                            : t("managePlan.checkoutPreview.emptyNational")}
                    </Text.caption>
                )}

                {!isInternational && methodOptions.length > 0 && (
                    <div className="checkout-preview-art" aria-hidden="true">
                        <ButtonGroup
                            grow
                            size="small"
                            options={methodOptions}
                            value={selectedMethod}
                        />
                    </div>
                )}

                {isInternational && (
                    <div className="checkout-preview-countries">
                        <Text.subheading contrast={6}>{t("managePlan.checkoutPreview.countriesTitle")}</Text.subheading>
                        <Text.caption contrast={5}>
                            {t("managePlan.checkoutPreview.countriesCount", { count: internationalCountries.length })}
                        </Text.caption>

                        <div className="checkout-preview-countries-grid">
                            {(showAllCountries
                                ? internationalCountries
                                : internationalCountries.slice(0, 9)
                            ).map(country => (
                                <div className="checkout-preview-country" key={country.name}>
                                    <img
                                        className="checkout-preview-country-flag"
                                        src={`https://flagcdn.com/${country.iso}.svg`}
                                        width="16"
                                        alt=""
                                        loading="lazy"
                                    />
                                    <Text.caption contrast={6}>{country.name}</Text.caption>
                                </div>
                            ))}
                        </div>

                        <Button
                            className="checkout-preview-countries-toggle"
                            variant="text"
                            color="inverse"
                            size="small"
                            preventLoading
                            onClick={() => setShowAllCountries(current => !current)}
                        >
                            {showAllCountries
                                ? t("managePlan.checkoutPreview.showLess")
                                : t("managePlan.checkoutPreview.showAll", { count: internationalCountries.length })}
                        </Button>
                    </div>
                )}

                {showInstallmentsSection && (
                    <div className="checkout-preview-installments">
                        <Text.subheading contrast={6}>
                            {t("managePlan.checkoutPreview.installmentsTitle")}
                        </Text.subheading>

                        {simulation?.loading ? (
                            <div className="checkout-preview-grid">
                                <div className="checkout-preview-col">
                                    <Skeleton variant="text" />
                                    <Skeleton variant="text" />
                                    <Skeleton variant="text" />
                                </div>
                                <div className="checkout-preview-col">
                                    <Skeleton variant="text" />
                                    <Skeleton variant="text" />
                                    <Skeleton variant="text" width="70%" />
                                </div>
                            </div>
                        ) : installmentColumns.length > 0 ? (
                            <div
                                className={`checkout-preview-grid ${installmentColumns.length === 1 ? "single" : ""}`}
                            >
                                {installmentColumns.map((column, columnIndex) => (
                                    <div className="checkout-preview-col" key={columnIndex}>
                                        {column.map((total, rowIndex) => {
                                            const count = columnIndex * splitAt + rowIndex + 1;

                                            return (
                                                <div key={count} className="checkout-preview-row">
                                                    <Text.caption contrast={6}>{count}x</Text.caption>
                                                    <Text.span contrast={9}>
                                                        R$ {Parser.price.brl(Math.round(total / count))}
                                                    </Text.span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        ) : null}

                        {simulation?.error && (
                            <div className="checkout-preview-error">
                                <ErrorMsg>{simulation.error}</ErrorMsg>
                                <Button
                                    variant="text"
                                    color="inverse"
                                    size="small"
                                    preventLoading
                                    onClick={onRetry}
                                >
                                    {t("common:actions.retry")}
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                <div className="checkout-preview-art" aria-hidden="true">
                    <Button color="inverse" size="medium" fullWidth preventLoading>
                        {isLifetime
                            ? t("managePlan.checkoutPreview.buyLifetime")
                            : t("managePlan.checkoutPreview.subscribe", { label })}
                    </Button>
                </div>
            </div>
        </Card>
    );
}
